import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowRight, faUsers } from '@fortawesome/free-solid-svg-icons';
import CompanyLogo from './CompanyLogo';
import NewsletterLogo from './NewsletterLogo';
import { deriveNewsletterDisplay } from '../utils/newsletterLogo';
import '../css/PaidSponsorTable.css';

export interface PublicSponsorPreview {
    sponsorName: string;
    rootDomain: string;
    tags: string[];
    contentTags: string[];
    maskedContact: string | null;
    contactPersonName: string | null;
    contactPersonTitle: string | null;
    newsletterName: string | null;
    emailAddress?: string | null;
    audienceSize: number | null;
    dateAdded: string | null;
}

// The public, no-login version of PaidSponsorTable's real placement card —
// same classnames/layout/fields (sponsor + newsletter side, tags, audience,
// exact date) so a visitor previewing the database sees exactly what a
// subscriber sees, just with contact info masked and the outreach actions
// visually locked instead of removed outright. Deliberately a standalone
// presentational component rather than a mode of PaidSponsorTable itself —
// that component's fetch/filter/sort/outreach state isn't relevant here.
const PublicSponsorCard: React.FC<{ item: PublicSponsorPreview }> = ({ item }) => {
    const newsletterDisplay = item.newsletterName
        ? deriveNewsletterDisplay(item.newsletterName, item.emailAddress || undefined)
        : undefined;

    const personName = item.contactPersonName?.trim();
    const personTitle = item.contactPersonTitle?.trim();

    const audienceLabel = item.audienceSize
        ? (item.audienceSize >= 1000 ? `${Math.round(item.audienceSize / 1000).toLocaleString()}K` : `${item.audienceSize}`)
        : null;

    return (
        <div className="placement-card">
            <div className="placement-card__header">
                <CompanyLogo domain={item.rootDomain} name={item.sponsorName} size={36} />
                <a
                    href={`https://${item.rootDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="placement-card__name"
                >
                    {item.sponsorName}
                </a>
            </div>

            {item.tags.length > 0 && (
                <div className="placement-card__tags">
                    {item.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="placement-card__tag">{tag}</span>
                    ))}
                </div>
            )}

            <div className="sponsor-contact sponsor-contact--locked" title="Subscribe to see the real contact">
                {personName && <p className="sponsor-contact__name">{personName}</p>}
                <p className="sponsor-contact__title">
                    {personName ? personTitle : (personTitle || 'Contact unlocks with a subscription')}
                </p>
                <div className="sponsor-contact__box">
                    <FontAwesomeIcon icon={faLock} className="sponsor-contact__unverified" />
                    <span className="sponsor-contact__email">{item.maskedContact || 'Subscribe to unlock'}</span>
                </div>
            </div>

            <div className="placement-card__sponsorship">
                {item.newsletterName ? (
                    <>
                        <div className="placement-card__sponsorship-row">
                            <FontAwesomeIcon icon={faArrowRight} className="placement-card__sponsorship-icon" />
                            <NewsletterLogo domain={newsletterDisplay?.domain} size={20} />
                            <span className="placement-card__newsletter-name">{newsletterDisplay?.name}</span>
                            {audienceLabel && (
                                <span className="placement-card__audience">
                                    <FontAwesomeIcon icon={faUsers} /> {audienceLabel}
                                </span>
                            )}
                        </div>
                        {item.dateAdded && (
                            <div className="placement-card__date">
                                Sponsored {new Date(item.dateAdded).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="placement-card__date">No newsletter placement recorded yet</div>
                )}
            </div>

            <div className="outreach-controls">
                <button className="pitch-btn pitch-btn--locked" disabled title="Subscribe to generate a personalized pitch">
                    <FontAwesomeIcon icon={faLock} /> Generate personalized pitch
                </button>
            </div>
        </div>
    );
};

export default PublicSponsorCard;
